/**
 * Hero section for level/subject information pages.
 *
 * What: Renders a level-specific hero with eyebrow, headline, subheadline, and
 * the shared trial CTA pair.
 * Why: Provides immediate clarity about which level/subject the page covers and encourages trial booking.
 * When: Update when changing hero structure or CTA strategy for level pages.
 */
import { Paper, Stack, Typography } from '@mui/material';

import LandingSectionShell from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';
import LandingTrialCtas from '@/components/landing/LandingTrialCtas';

export interface LevelHeroContent {
  eyebrow?: string;
  headline: string;
  subheadline: string;
  supportingLine?: string;
}

interface LevelHeroSectionProps {
  content: LevelHeroContent;
  sourceLocation?: string;
}

function LevelHeroSection({
  content,
  sourceLocation = 'level_page_hero',
}: LevelHeroSectionProps) {
  return (
    <LandingSectionShell
      ariaLabelledby="level-hero-title"
      // The hero sits looser to the band below it than the standard rhythm.
      mb={{ xs: 6, sm: 8, md: 12 }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          p: { xs: 3, sm: 4, md: 5 },
          borderRadius: { xs: radii.lg, md: radii.xl },
          backgroundColor: colors.paper,
          border: `1px solid ${colors.border}`,
          boxShadow: shadows.hero,
        }}
      >
        <Stack
          spacing={3}
          alignItems="flex-start"
          sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
        >
          {content.eyebrow && (
            <Typography
              component="p"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.09em',
                fontWeight: 800,
                fontSize: 11.5,
                color: colors.accentCobalt,
                overflowWrap: 'break-word',
              }}
            >
              {content.eyebrow}
            </Typography>
          )}

          <Stack
            spacing={2.5}
            sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
          >
            <Typography
              id="level-hero-title"
              component="h1"
              sx={{
                fontSize: { xs: 32, sm: 38, md: 48, lg: 52 },
                // Sora tops out at 800 (src/styles/fonts.css); anything heavier
                // is browser-synthesized.
                fontWeight: 800,
                lineHeight: { xs: 1.15, md: 1.1 },
                letterSpacing: { xs: -0.6, sm: -0.8, md: -1.2 },
                color: colors.ink,
                maxWidth: { xs: '100%', sm: '26ch', md: '30ch' },
                overflowWrap: 'break-word',
              }}
            >
              {content.headline}
            </Typography>

            <Stack
              spacing={{ xs: 1.25, sm: 1.5 }}
              sx={{ width: '100%', minWidth: 0 }}
            >
              <Typography
                variant="h6"
                // Prose under the h1, not a section heading — `variant="h6"`
                // is here for the type scale only.
                component="p"
                sx={{
                  maxWidth: { xs: '100%', sm: 720 },
                  color: colors.inkMuted,
                  fontWeight: 650,
                  lineHeight: { xs: 1.45, sm: 1.4 },
                  fontSize: { xs: 17, sm: 20 },
                  overflowWrap: 'break-word',
                }}
              >
                {content.subheadline}
              </Typography>

              {content.supportingLine && (
                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: { xs: '100%', sm: 720 },
                    color: colors.inkSubtle,
                    fontStyle: 'italic',
                    fontSize: { xs: 14, sm: 16 },
                    lineHeight: { xs: 1.55, sm: 1.5 },
                    overflowWrap: 'break-word',
                  }}
                >
                  {content.supportingLine}
                </Typography>
              )}
            </Stack>
          </Stack>

          <LandingTrialCtas
            sourceLocation={sourceLocation}
            sx={{ pt: { xs: 2, sm: 0.5 }, width: '100%' }}
          />
        </Stack>
      </Paper>
    </LandingSectionShell>
  );
}

export default LevelHeroSection;
